/**
 * Pet Bundle Builder v3 — Custom Element for Shopify Dawn
 *
 * v3 changes:
 * - Eye color: color-coded pill chips (not dropdown)
 * - Pet count: simplified 1,2,3,4 circles + "Add a Pet" button
 * - Upload: "Click to Upload" button-style with camera emoji
 * - Error auto-clear: red outlines clear on user interaction
 * - Card header: numbered circle badge + "Pet N"
 * - Warm palette design
 */

if (!customElements.get('pet-bundle-builder')) {
  class PetBundleBuilder extends HTMLElement {
    constructor() {
      super();
      this._config = null;
      this._petCounter = 1;
      this._currentPetCount = 1;
      this._filePondInstances = new Map();
      this._uploadTimeouts = new Map();
      this._isSubmitting = false;
      this._filepondReady = false;
    }

    /* ------------------------------------------------------------------ */
    /*  Lifecycle                                                          */
    /* ------------------------------------------------------------------ */

    connectedCallback() {
      this.sectionId = this.dataset.sectionId;

      // Cache DOM
      this.cardsContainer = this.querySelector(`#PetCards-${this.sectionId}`);
      this.summaryLines = this.querySelector(`#SummaryLines-${this.sectionId}`);
      this.summaryDiscount = this.querySelector(`#SummaryDiscount-${this.sectionId}`);
      this.totalPrice = this.querySelector(`#TotalPrice-${this.sectionId}`);
      this.listPrice = this.querySelector(`#ListPrice-${this.sectionId}`);
      this.addToCartBtn = this.querySelector(`#AddToCart-${this.sectionId}`);
      this.buyNowBtn = this.querySelector(`#BuyNow-${this.sectionId}`);
      this.errorEl = this.querySelector(`#PetError-${this.sectionId}`);
      this.tierBadges = this.querySelector(`#TierBadges-${this.sectionId}`);
      this.template = this.querySelector(`#PetCardTemplate-${this.sectionId}`);
      this.priceDisplay = this.querySelector(`#PriceDisplay-${this.sectionId}`);
      this.countSelector = this.querySelector(`#PetCountSelector-${this.sectionId}`);
      this.addPetBtn = this.querySelector(`#AddPet-${this.sectionId}`);
      this.savingsCallout = this.querySelector(`#SavingsCallout-${this.sectionId}`);
      this.savingsAmount = this.querySelector(`#SavingsAmount-${this.sectionId}`);
      this.savingsCalloutTier = this.querySelector(`#SavingsCalloutTier-${this.sectionId}`);
      this.savingsAmountTier = this.querySelector(`#SavingsAmountTier-${this.sectionId}`);

      // Event delegation
      this.addEventListener('click', this._handleClick.bind(this));
      this.addEventListener('input', this._handleInput.bind(this));
      this.addEventListener('change', this._handleChange.bind(this));

      // Load FilePond programmatically
      this._loadFilePond();

      // Apply default eye color to initial cards based on current species
      this.cardsContainer.querySelectorAll('.pet-card').forEach(card => {
        const activeSpecies = card.querySelector('.pet-card__species-pill--active');
        const species = activeSpecies?.dataset.petType || 'Dog';
        this._applyDefaultEyeColor(card, species);
      });

      // Restore saved form state (if any, < 24h old)
      this._restoreState();

      // Initial summary
      this._updateSummary();
    }

    disconnectedCallback() {
      this._filePondInstances.forEach((pond) => {
        try { pond.destroy(); } catch (e) { /* */ }
      });
      this._filePondInstances.clear();
      if (this._outsideClickHandler) {
        document.removeEventListener('click', this._outsideClickHandler);
        this._outsideClickHandler = null;
      }
    }

    /* ------------------------------------------------------------------ */
    /*  Configuration                                                      */
    /* ------------------------------------------------------------------ */

    get config() {
      if (!this._config) {
        const el = document.getElementById(`PetBundleConfig-${this.sectionId}`);
        if (el) {
          try { this._config = JSON.parse(el.textContent); }
          catch (e) { console.error('PetBundleBuilder: config parse error', e); this._config = {}; }
        }
      }
      return this._config;
    }

    /* ------------------------------------------------------------------ */
    /*  FilePond — Programmatic Loading                                    */
    /* ------------------------------------------------------------------ */

    _loadFilePond() {
      const cdnBase = 'https://unpkg.com';
      const scripts = [
        `${cdnBase}/filepond@4.31.4/dist/filepond.js`,
        `${cdnBase}/filepond-plugin-image-preview@4.6.12/dist/filepond-plugin-image-preview.js`,
        `${cdnBase}/filepond-plugin-file-validate-type@1.2.9/dist/filepond-plugin-file-validate-type.js`,
        `${cdnBase}/filepond-plugin-file-validate-size@2.2.8/dist/filepond-plugin-file-validate-size.js`,
      ];

      this._loadScript(scripts[0]).then(() => {
        return Promise.all(scripts.slice(1).map(src => this._loadScript(src)));
      }).then(() => {
        this._filepondReady = true;
        this._registerFilePondPlugins();
        const firstCard = this.cardsContainer.querySelector('.pet-card');
        if (firstCard) this._initFilePondForCard(firstCard);
      }).catch(err => {
        console.warn('PetBundleBuilder: FilePond load failed', err);
      });
    }

    _loadScript(src) {
      return new Promise((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) {
          setTimeout(resolve, 50);
          return;
        }
        const s = document.createElement('script');
        s.src = src;
        s.onload = resolve;
        s.onerror = reject;
        document.head.appendChild(s);
      });
    }

    _registerFilePondPlugins() {
      if (!window.FilePond) return;
      const plugins = [];
      if (window.FilePondPluginImagePreview) plugins.push(window.FilePondPluginImagePreview);
      if (window.FilePondPluginFileValidateType) plugins.push(window.FilePondPluginFileValidateType);
      if (window.FilePondPluginFileValidateSize) plugins.push(window.FilePondPluginFileValidateSize);
      if (plugins.length) window.FilePond.registerPlugin(...plugins);
    }

    /**
     * Compress an image file client-side using canvas.
     * Target: max dimension 1600px, JPEG quality 85%.
     * Falls back to original file if compression fails or file is not an image.
     * @param {File} file
     * @returns {Promise<File|Blob>} — compressed blob or original file
     */
    _compressImage(file) {
      return new Promise((resolve) => {
        try {
          if (!file || !file.type || !file.type.startsWith('image/')) {
            return resolve(file);
          }
          // Skip compression for small files (< 500KB) — not worth the overhead
          if (file.size < 500 * 1024) return resolve(file);

          const MAX_DIM = 1600;
          const QUALITY = 0.85;
          const url = URL.createObjectURL(file);
          const img = new Image();

          img.onload = () => {
            try {
              let { width, height } = img;
              const scale = Math.min(1, MAX_DIM / Math.max(width, height));
              width = Math.round(width * scale);
              height = Math.round(height * scale);

              const canvas = document.createElement('canvas');
              canvas.width = width;
              canvas.height = height;
              const ctx = canvas.getContext('2d');
              ctx.drawImage(img, 0, 0, width, height);
              URL.revokeObjectURL(url);

              // Prefer JPEG for compression; fall back to original type for PNGs with transparency needs.
              // For pet photos, JPEG is almost always fine.
              canvas.toBlob(
                (blob) => {
                  if (!blob) return resolve(file);
                  // Only use compressed version if actually smaller
                  if (blob.size >= file.size) return resolve(file);
                  // Wrap in File so FilePond treats it cleanly
                  const compressed = new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), {
                    type: 'image/jpeg',
                    lastModified: Date.now()
                  });
                  resolve(compressed);
                },
                'image/jpeg',
                QUALITY
              );
            } catch (e) {
              URL.revokeObjectURL(url);
              resolve(file);
            }
          };
          img.onerror = () => {
            URL.revokeObjectURL(url);
            resolve(file);
          };
          img.src = url;
        } catch (e) {
          resolve(file);
        }
      });
    }

    _getFilePondServerConfig() {
      const config = this.config;
      const compress = (file) => this._compressImage(file);

      const makeProcessor = (url, buildFormData, parseResponse) => {
        return (fieldName, file, metadata, load, error, progress, abort) => {
          let xhr;
          let aborted = false;

          compress(file).then((uploadFile) => {
            if (aborted) return;
            const fd = buildFormData(uploadFile);
            xhr = new XMLHttpRequest();
            xhr.open('POST', url);
            xhr.upload.onprogress = (e) => progress(e.lengthComputable, e.loaded, e.total);
            xhr.onload = function () {
              if (xhr.status >= 200 && xhr.status < 300) {
                try { load(parseResponse(xhr.responseText)); }
                catch (e) { error('Parse error'); }
              } else { error('Upload failed'); }
            };
            xhr.onerror = () => error('Upload failed');
            xhr.send(fd);
          }).catch(() => {
            if (!aborted) error('Upload failed');
          });

          return {
            abort: () => {
              aborted = true;
              if (xhr) xhr.abort();
              abort();
            }
          };
        };
      };

      if (config.uploadEndpoint) {
        return {
          process: makeProcessor(
            config.uploadEndpoint,
            (file) => { const fd = new FormData(); fd.append('file', file, file.name); return fd; },
            (text) => {
              try { const r = JSON.parse(text); return r.secure_url || r.url || text; }
              catch (e) { return text; }
            }
          )
        };
      }

      if (config.uploadCloudName && config.uploadPreset) {
        const url = `https://api.cloudinary.com/v1_1/${config.uploadCloudName}/image/upload`;
        return {
          process: makeProcessor(
            url,
            (file) => {
              const fd = new FormData();
              fd.append('file', file);
              fd.append('upload_preset', config.uploadPreset);
              return fd;
            },
            (text) => {
              const r = JSON.parse(text);
              return r.secure_url || r.url;
            }
          )
        };
      }

      return null;
    }

    _initFilePondForCard(card) {
      if (!card || !this._filepondReady || !window.FilePond) return;

      const fileInput = card.querySelector('.pet-card__file-input');
      if (!fileInput) return;

      const serverConfig = this._getFilePondServerConfig();
      const uploadUrlInput = card.querySelector('.pet-card__upload-url');
      const cardIndex = card.dataset.petIndex;

      this._destroyFilePondForCard(cardIndex);

      const opts = {
        allowMultiple: false,
        maxFiles: 1,
        acceptedFileTypes: ['image/jpeg', 'image/png', 'image/webp'],
        maxFileSize: '10MB',
        labelIdle: '\uD83D\uDCF7 <span class="filepond--label-action">Click to Upload</span>',
        labelMaxFileSizeExceeded: 'Photo is too large',
        labelMaxFileSize: 'Max size is 10MB',
        imagePreviewHeight: 140,
        stylePanelLayout: 'compact',
        credits: false,
      };

      if (serverConfig) opts.server = serverConfig;

      const pond = window.FilePond.create(fileInput, opts);

      pond.on('processfile', (error, file) => {
        // Clear upload timeout on success or handled failure
        const timeoutId = this._uploadTimeouts.get(String(cardIndex));
        if (timeoutId) { clearTimeout(timeoutId); this._uploadTimeouts.delete(String(cardIndex)); }

        if (!error && file.serverId && uploadUrlInput) {
          uploadUrlInput.value = file.serverId;
          const zone = card.querySelector('.pet-card__upload-zone');
          if (zone) zone.classList.remove('pet-card__upload-zone--error');
        }

        // Hide progress indicator on completion
        this._updateUploadProgress(card, null);

        // Persist state now that we have the final URL
        this._saveState();
      });

      // Update custom progress indicator + reset stall timeout on real progress
      pond.on('processfileprogress', (file, progress) => {
        this._updateUploadProgress(card, progress);
        // Reset stall timeout — upload is actively progressing
        this._resetUploadTimeout(cardIndex, card);
      });

      // Local fallback: store data URL
      if (!serverConfig) {
        pond.on('addfile', (error, file) => {
          if (!error && file.file && uploadUrlInput) {
            const reader = new FileReader();
            reader.onload = (e) => {
              uploadUrlInput.value = e.target.result;
              this._updateCardBadgeThumbnails();
              this._updateSummary();
            };
            reader.readAsDataURL(file.file);
            const zone = card.querySelector('.pet-card__upload-zone');
            if (zone) zone.classList.remove('pet-card__upload-zone--error');
          }
        });
      } else {
        // For server upload: also trigger thumbnail update when file is added locally
        pond.on('addfile', (error, file) => {
          if (!error && file.file) {
            const zone = card.querySelector('.pet-card__upload-zone');
            if (zone) zone.classList.remove('pet-card__upload-zone--error');
            // Create a small thumbnail data URL just for UI (not for submission)
            const reader = new FileReader();
            reader.onload = (e) => {
              card.dataset.thumbUrl = e.target.result;
              this._updateCardBadgeThumbnails();
              this._updateSummary();
            };
            reader.readAsDataURL(file.file);

            // Start stall-timeout — fires only if no upload progress events
            // arrive within the window (reset by processfileprogress).
            this._resetUploadTimeout(cardIndex, card);
          }
        });
      }

      pond.on('removefile', () => {
        if (uploadUrlInput) uploadUrlInput.value = '';
        delete card.dataset.thumbUrl;
        // Clear any pending upload timeout
        const pendingTimeout = this._uploadTimeouts.get(String(cardIndex));
        if (pendingTimeout) { clearTimeout(pendingTimeout); this._uploadTimeouts.delete(String(cardIndex)); }
        // Hide progress indicator
        this._updateUploadProgress(card, null);
        this._updateCardBadgeThumbnails();
        this._updateSummary();
        this._saveState();
      });

      // Surface upload errors so users know their photo didn't save
      pond.on('processfilerevert', () => {
        if (uploadUrlInput) uploadUrlInput.value = '';
        this._updateUploadProgress(card, null);
      });
      pond.on('error', (error) => {
        const num = parseInt(card.dataset.petIndex, 10) + 1;
        this._showError(`Pet ${num}: Photo upload failed — please remove and re-upload your photo.`);
        this._trackUploadIssue('upload_error', { pet: num, error: String(error) });
        if (uploadUrlInput) uploadUrlInput.value = '';
        this._updateUploadProgress(card, null);
      });

      this._filePondInstances.set(String(cardIndex), pond);
    }

    _destroyFilePondForCard(cardIndex) {
      const key = String(cardIndex);
      const pond = this._filePondInstances.get(key);
      if (pond) {
        try { pond.destroy(); } catch (e) { /* */ }
        this._filePondInstances.delete(key);
      }
    }

    /* ------------------------------------------------------------------ */
    /*  Event Delegation                                                   */
    /* ------------------------------------------------------------------ */

    _handleClick(event) {
      const t = event.target;

      // Species pill
      if (t.closest('.pet-card__species-pill')) {
        this._handleSpeciesToggle(t.closest('.pet-card__species-pill'));
      }
      // Eye color pill
      else if (t.closest('.pet-card__eye-pill')) {
        this._handleEyeColorPill(t.closest('.pet-card__eye-pill'));
      }
      // Quantity buttons
      else if (t.closest('.pet-card__qty-minus')) {
        this._handleQuantityChange(t.closest('.pet-card__qty-minus'), -1);
      }
      else if (t.closest('.pet-card__qty-plus')) {
        this._handleQuantityChange(t.closest('.pet-card__qty-plus'), 1);
      }
      // Remove card
      else if (t.closest('.pet-card__remove')) {
        this._removePetCard(t.closest('.pet-card__remove').dataset.petIndex);
      }
      // Tier badge click (sets pet count)
      else if (t.closest('.pet-bundle__tier-badge')) {
        this._handleTierBadgeClick(t.closest('.pet-bundle__tier-badge'));
      }
      // Pet count pills (1–4)
      else if (t.closest('.pet-bundle__count-pill')) {
        this._handleCountPillClick(t.closest('.pet-bundle__count-pill'));
      }
      // "+" more button — toggle dropdown
      else if (t.closest('.pet-bundle__count-more-btn')) {
        this._toggleMoreDropdown();
      }
      // Dropdown option (5–10)
      else if (t.closest('.pet-bundle__count-more-option')) {
        this._handleMoreOptionClick(t.closest('.pet-bundle__count-more-option'));
      }
      // Add a Pet button
      else if (t.closest('.pet-bundle__add-pet')) {
        this._handleAddPet();
      }
      // Buy now
      else if (t.closest('.pet-bundle__submit--buy-now')) {
        this._buyNow();
      }
      // Add to cart
      else if (t.closest('.pet-bundle__submit')) {
        this._addToCart();
      }
    }

    _handleInput(event) {
      const t = event.target;

      // Auto-clear errors on input
      if (t.closest('.pet-card__name-input')) {
        t.closest('.pet-card__name-input').classList.remove('pet-card__input--error');
        this._updateSummary();
      }
      else if (t.closest('.pet-card__qty-input')) {
        this._updateSummary();
      }
    }

    _handleChange(event) {
      if (event.target.closest('.pet-card__qty-input')) {
        const input = event.target.closest('.pet-card__qty-input');
        let val = parseInt(input.value, 10);
        if (isNaN(val) || val < 1) val = 1;
        if (val > 99) val = 99;
        input.value = val;
        this._updateSummary();
      }
    }

    /* ------------------------------------------------------------------ */
    /*  Eye Color Pill Handler                                             */
    /* ------------------------------------------------------------------ */

    _handleEyeColorPill(pill) {
      const card = pill.closest('.pet-card');
      const pillsContainer = card.querySelector('.pet-card__eye-pills');
      const hiddenInput = card.querySelector('.pet-card__eye-value');

      // Toggle active
      pillsContainer.querySelectorAll('.pet-card__eye-pill').forEach(p => {
        p.classList.remove('pet-card__eye-pill--active');
      });
      pill.classList.add('pet-card__eye-pill--active');

      // Update hidden input
      if (hiddenInput) hiddenInput.value = pill.dataset.color;

      // Mark that user has explicitly chosen — don't auto-override on species toggle
      card.dataset.eyeUserChose = 'true';

      // Auto-clear error
      pillsContainer.classList.remove('pet-card__eye-pills--error');

      this._updateSummary();
    }

    /**
     * Pre-select the default eye color for a species if the user hasn't
     * explicitly chosen one yet.
     * @param {HTMLElement} card
     * @param {'Dog'|'Cat'} species
     */
    _applyDefaultEyeColor(card, species) {
      // Respect explicit user choice
      if (card.dataset.eyeUserChose === 'true') return;

      const defaultName = species === 'Cat' ? 'green' : 'brown';
      const pillsContainer = card.querySelector('.pet-card__eye-pills');
      const hiddenInput = card.querySelector('.pet-card__eye-value');
      if (!pillsContainer || !hiddenInput) return;

      const pills = pillsContainer.querySelectorAll('.pet-card__eye-pill');
      let targetPill = null;
      pills.forEach(p => {
        if ((p.dataset.color || '').toLowerCase() === defaultName) targetPill = p;
      });
      // Fallback to first pill if exact name not found
      if (!targetPill) targetPill = pills[0];
      if (!targetPill) return;

      pills.forEach(p => p.classList.remove('pet-card__eye-pill--active'));
      targetPill.classList.add('pet-card__eye-pill--active');
      hiddenInput.value = targetPill.dataset.color || '';
    }

    /* ------------------------------------------------------------------ */
    /*  Pet Count Selector (1, 2, 3, 4)                                    */
    /* ------------------------------------------------------------------ */

    _handleCountPillClick(pill) {
      const count = parseInt(pill.dataset.count, 10);
      if (isNaN(count)) return;

      // Close dropdown if open
      this._closeMoreDropdown();

      this.countSelector.querySelectorAll('.pet-bundle__count-pill').forEach(p => {
        p.classList.remove('pet-bundle__count-pill--active');
      });
      pill.classList.add('pet-bundle__count-pill--active');

      // Deactivate the "+" button
      const moreBtn = this.countSelector.querySelector('.pet-bundle__count-more-btn');
      if (moreBtn) moreBtn.classList.remove('pet-bundle__count-more-btn--active');

      this._setPetCount(count);
    }

    _toggleMoreDropdown() {
      const dropdown = this.countSelector.querySelector('.pet-bundle__count-more-dropdown');
      if (!dropdown) return;
      const isOpen = !dropdown.hidden;
      if (isOpen) {
        this._closeMoreDropdown();
      } else {
        dropdown.hidden = false;
        // Close on outside click
        setTimeout(() => {
          this._outsideClickHandler = (e) => {
            if (!e.target.closest('.pet-bundle__count-more')) {
              this._closeMoreDropdown();
            }
          };
          document.addEventListener('click', this._outsideClickHandler, { once: true });
        }, 0);
      }
    }

    _closeMoreDropdown() {
      const dropdown = this.countSelector.querySelector('.pet-bundle__count-more-dropdown');
      if (dropdown) dropdown.hidden = true;
      if (this._outsideClickHandler) {
        document.removeEventListener('click', this._outsideClickHandler);
        this._outsideClickHandler = null;
      }
    }

    _handleMoreOptionClick(option) {
      const count = parseInt(option.dataset.count, 10);
      if (isNaN(count)) return;

      this._closeMoreDropdown();

      // Deactivate all 1–4 pills
      this.countSelector.querySelectorAll('.pet-bundle__count-pill').forEach(p => {
        p.classList.remove('pet-bundle__count-pill--active');
      });

      // Activate the "+" button and highlight the chosen option
      const moreBtn = this.countSelector.querySelector('.pet-bundle__count-more-btn');
      if (moreBtn) moreBtn.classList.add('pet-bundle__count-more-btn--active');

      // Clear all option highlights, set active one
      this.countSelector.querySelectorAll('.pet-bundle__count-more-option').forEach(o => {
        o.classList.toggle('pet-bundle__count-more-option--active', parseInt(o.dataset.count, 10) === count);
      });

      this._setPetCount(count);
    }

    _handleTierBadgeClick(badge) {
      const tier = parseInt(badge.dataset.tier, 10);
      if (isNaN(tier)) return;

      // Tier 4 means "4+ pets" — set to exactly 4
      const count = tier;
      this._setPetCount(count);
      this._syncCountSelector();
    }

    _handleAddPet() {
      this._addPetCard();
      this._syncCountSelector();
    }

    _setPetCount(count) {
      const currentCards = this.cardsContainer.querySelectorAll('.pet-card');
      const currentCount = currentCards.length;

      if (count === currentCount) return;

      if (count > currentCount) {
        for (let i = currentCount; i < count; i++) {
          this._addPetCard();
        }
      } else {
        const toRemove = Array.from(currentCards).slice(count);
        toRemove.forEach(card => {
          const idx = card.dataset.petIndex;
          this._destroyFilePondForCard(idx);
          card.remove();
        });
        this._reindexCards();
        this._updateSummary();
      }

      this._currentPetCount = count;
    }

    /* ------------------------------------------------------------------ */
    /*  Species Toggle (compact pills)                                     */
    /* ------------------------------------------------------------------ */

    _handleSpeciesToggle(pill) {
      const card = pill.closest('.pet-card');
      const pills = card.querySelector('.pet-card__species-pills');
      const hiddenInput = card.querySelector('.pet-card__variant-input');

      pills.querySelectorAll('.pet-card__species-pill').forEach(p => {
        p.classList.remove('pet-card__species-pill--active');
      });
      pill.classList.add('pet-card__species-pill--active');

      if (hiddenInput) hiddenInput.value = pill.dataset.variantId;

      // Update eye color default if user hasn't explicitly chosen one
      this._applyDefaultEyeColor(card, pill.dataset.petType);

      this._swapGalleryMedia(pill.dataset.variantId);
      this._updateSummary();
    }

    _swapGalleryMedia(variantId) {
      const mediaMap = this.config.variantMediaMap;
      if (!mediaMap) return;

      const mediaId = mediaMap[variantId];
      if (!mediaId) return;

      const gallery = this.querySelector('media-gallery');
      if (gallery && typeof gallery.setActiveMedia === 'function') {
        // Dawn's setActiveMedia calls window.scrollTo to scroll the gallery
        // into view. Temporarily block all scrollTo/scroll calls so the user
        // stays where they are in the form.
        const origScrollTo = window.scrollTo;
        const origScroll = window.scroll;
        window.scrollTo = () => {};
        window.scroll = () => {};
        gallery.setActiveMedia(mediaId, false);
        // Restore after Dawn's sync + async scroll attempts complete.
        setTimeout(() => {
          window.scrollTo = origScrollTo;
          window.scroll = origScroll;
        }, 100);
      }
    }

    /* ------------------------------------------------------------------ */
    /*  Quantity                                                            */
    /* ------------------------------------------------------------------ */

    _handleQuantityChange(btn, delta) {
      const card = btn.closest('.pet-card');
      const input = card.querySelector('.pet-card__qty-input');
      let val = parseInt(input.value, 10) || 1;
      val = Math.max(1, Math.min(99, val + delta));
      input.value = val;
      this._updateSummary();
    }

    /* ------------------------------------------------------------------ */
    /*  Card Management                                                    */
    /* ------------------------------------------------------------------ */

    _addPetCard() {
      if (!this.template) return;

      this._petCounter++;
      const clone = this.template.content.cloneNode(true);
      const card = clone.querySelector('.pet-card');

      card.dataset.petIndex = this._petCounter;

      // Update badge and heading
      const badge = card.querySelector('.pet-card__badge');
      if (badge) badge.textContent = this._petCounter;

      const heading = card.querySelector('.pet-card__number');
      if (heading) heading.textContent = `Pet ${this._petCounter}`;

      const removeBtn = card.querySelector('.pet-card__remove');
      if (removeBtn) removeBtn.dataset.petIndex = this._petCounter;

      // Update IDs
      card.querySelectorAll('[id]').forEach(el => {
        el.id = el.id.replace(/-0$/, `-${this._petCounter}`);
      });
      card.querySelectorAll('[for]').forEach(el => {
        el.setAttribute('for', el.getAttribute('for').replace(/-0$/, `-${this._petCounter}`));
      });

      // Reset values
      const nameInput = card.querySelector('.pet-card__name-input');
      if (nameInput) nameInput.value = '';

      const eyeValue = card.querySelector('.pet-card__eye-value');
      if (eyeValue) eyeValue.value = '';

      // Reset eye color pills
      card.querySelectorAll('.pet-card__eye-pill').forEach(p => {
        p.classList.remove('pet-card__eye-pill--active');
      });

      // Clear user-chose flag so default applies
      card.dataset.eyeUserChose = 'false';

      const qtyInput = card.querySelector('.pet-card__qty-input');
      if (qtyInput) qtyInput.value = '1';

      const uploadUrl = card.querySelector('.pet-card__upload-url');
      if (uploadUrl) uploadUrl.value = '';

      // Reset species to Dog
      card.querySelectorAll('.pet-card__species-pill').forEach(p => {
        p.classList.remove('pet-card__species-pill--active');
        if (p.dataset.petType === 'Dog') p.classList.add('pet-card__species-pill--active');
      });
      const variantInput = card.querySelector('.pet-card__variant-input');
      if (variantInput) variantInput.value = this.config.dogVariantId;

      card.classList.add('pet-card--entering');

      this.cardsContainer.appendChild(clone);

      // Init FilePond on new card
      const addedCard = this.cardsContainer.querySelector(`.pet-card[data-pet-index="${this._petCounter}"]`);
      if (addedCard && this._filepondReady) {
        this._initFilePondForCard(addedCard);
      }

      // Apply default eye color based on species (Dog=Brown default)
      if (addedCard) this._applyDefaultEyeColor(addedCard, 'Dog');

      this._reindexCards();
      this._updateSummary();
    }

    _removePetCard(index) {
      const card = this.cardsContainer.querySelector(`.pet-card[data-pet-index="${index}"]`);
      if (!card) return;

      this._destroyFilePondForCard(index);

      card.classList.add('pet-card--removing');
      card.addEventListener('animationend', () => {
        card.remove();
        this._reindexCards();
        this._updateSummary();
        this._syncCountSelector();
      }, { once: true });
    }

    _reindexCards() {
      const cards = this.cardsContainer.querySelectorAll('.pet-card');
      cards.forEach((card, i) => {
        const badge = card.querySelector('.pet-card__badge');
        if (badge) badge.textContent = i + 1;

        const heading = card.querySelector('.pet-card__number');
        if (heading) heading.textContent = `Pet ${i + 1}`;
      });

      // Ensure first card is not removable
      if (cards.length > 0) {
        const firstRemoveBtn = cards[0].querySelector('.pet-card__remove');
        if (firstRemoveBtn) firstRemoveBtn.style.display = cards.length > 1 ? '' : 'none';
      }
    }

    _syncCountSelector() {
      const count = this.cardsContainer.querySelectorAll('.pet-card').length;
      this._currentPetCount = count;

      // Highlight 1–4 pills
      const pills = this.countSelector.querySelectorAll('.pet-bundle__count-pill');
      pills.forEach(p => {
        const c = parseInt(p.dataset.count, 10);
        p.classList.toggle('pet-bundle__count-pill--active', c === count);
      });

      // Handle 5+ via the "+" button
      const moreBtn = this.countSelector.querySelector('.pet-bundle__count-more-btn');
      if (count > 4) {
        if (moreBtn) moreBtn.classList.add('pet-bundle__count-more-btn--active');
        this.countSelector.querySelectorAll('.pet-bundle__count-more-option').forEach(o => {
          o.classList.toggle('pet-bundle__count-more-option--active', parseInt(o.dataset.count, 10) === count);
        });
      } else {
        if (moreBtn) moreBtn.classList.remove('pet-bundle__count-more-btn--active');
        this.countSelector.querySelectorAll('.pet-bundle__count-more-option').forEach(o => {
          o.classList.remove('pet-bundle__count-more-option--active');
        });
      }

      this._updateCardBadgeThumbnails();
    }

    _updateCardBadgeThumbnails() {
      const cards = this.cardsContainer.querySelectorAll('.pet-card');

      cards.forEach((card, i) => {
        const badge = card.querySelector('.pet-card__badge');
        if (!badge) return;

        const uploadUrl = card.querySelector('.pet-card__upload-url')?.value || '';
        const thumbUrl = card.dataset.thumbUrl || '';
        const src = thumbUrl || (uploadUrl && uploadUrl.startsWith('data:') ? uploadUrl : '');

        if (src) {
          badge.classList.add('pet-card__badge--has-thumb');
          if (!badge.querySelector('img')) {
            badge.innerHTML = `<img src="${src}" alt="Pet ${i + 1}">`;
          } else {
            badge.querySelector('img').src = src;
          }
        } else {
          badge.classList.remove('pet-card__badge--has-thumb');
          badge.textContent = String(i + 1);
        }
      });
    }

    /* ------------------------------------------------------------------ */
    /*  Data Extraction                                                    */
    /* ------------------------------------------------------------------ */

    _getCardData(card) {
      const activePill = card.querySelector('.pet-card__species-pill--active');
      const uploadUrl = card.querySelector('.pet-card__upload-url')?.value || '';
      const thumbUrl = card.dataset.thumbUrl || '';
      return {
        variantId: card.querySelector('.pet-card__variant-input')?.value || this.config.dogVariantId,
        petType: activePill?.dataset.petType || 'Dog',
        petName: card.querySelector('.pet-card__name-input')?.value.trim() || '',
        eyeColor: card.querySelector('.pet-card__eye-value')?.value || '',
        uploadUrl: uploadUrl,
        thumbUrl: thumbUrl || (uploadUrl && uploadUrl.startsWith('data:') ? uploadUrl : ''),
        quantity: Math.max(1, parseInt(card.querySelector('.pet-card__qty-input')?.value, 10) || 1),
        index: card.dataset.petIndex,
      };
    }

    _getAllCardData() {
      return Array.from(this.cardsContainer.querySelectorAll('.pet-card')).map(c => this._getCardData(c));
    }

    _getTotalPetCount() {
      return this._getAllCardData().reduce((sum, p) => sum + p.quantity, 0);
    }

    /* ------------------------------------------------------------------ */
    /*  Discount Calculation                                               */
    /* ------------------------------------------------------------------ */

    _getDiscountAmountCents(totalPets) {
      const t = this.config.discountTiers;
      const perPet = this.config.discountPerPetAbove5 || 1100; // $11/pet default
      if (totalPets > 5) return (t['5'] || 0) + ((totalPets - 5) * perPet);
      if (totalPets === 5) return t['5'] || 0;
      if (totalPets === 4) return t['4'] || 0;
      if (totalPets === 3) return t['3'] || 0;
      if (totalPets === 2) return t['2'] || 0;
      return 0;
    }

    _getDiscountedTotalCents(totalPets) {
      return (this.config.basePrice * totalPets) - this._getDiscountAmountCents(totalPets);
    }

    /* ------------------------------------------------------------------ */
    /*  Money Formatting                                                   */
    /* ------------------------------------------------------------------ */

    _formatMoney(cents) {
      const amount = (cents / 100).toFixed(2);
      const noDecimals = Math.round(cents / 100).toString();
      const withComma = amount.replace('.', ',');
      let fmt = this.config.moneyFormat || '${{amount}}';
      return fmt
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withComma)
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, noDecimals.replace(/\B(?=(\d{3})+(?!\d))/g, ','))
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDecimals)
        .replace(/\{\{\s*amount\s*\}\}/g, amount);
    }

    /* ------------------------------------------------------------------ */
    /*  Summary & Tier Updates                                             */
    /* ------------------------------------------------------------------ */

    _updateSummary() {
      const petData = this._getAllCardData();
      const totalPets = petData.reduce((sum, p) => sum + p.quantity, 0);
      const discountAmount = this._getDiscountAmountCents(totalPets);
      const bundleTotal = this._getDiscountedTotalCents(totalPets);
      const basePrice = this.config.basePrice;

      this._updateTierBadges(totalPets);
      this._updateSavingsCallout(totalPets);
      this._updateCardProgress();
      this._updateCardBadgeThumbnails();

      // Price display
      if (this.priceDisplay) {
        if (discountAmount > 0) {
          const perUnit = Math.round(bundleTotal / totalPets);
          this.priceDisplay.innerHTML = `${this._formatMoney(perUnit)} each <span class="pet-bundle__price-compare">${this._formatMoney(basePrice)}</span>`;
        } else {
          this.priceDisplay.textContent = this._formatMoney(basePrice);
        }
      }

      // Summary lines (with optional photo thumbnails)
      if (this.summaryLines) {
        this.summaryLines.innerHTML = petData.map((pet, i) => {
          const name = pet.petName || `Pet ${i + 1}`;
          const lineTotal = basePrice * pet.quantity;
          const thumbHtml = pet.thumbUrl
            ? `<img class="pet-bundle__summary-thumb" src="${pet.thumbUrl}" alt="${this._esc(name)}">`
            : '';
          return `<div class="pet-bundle__summary-line">
            <span class="pet-bundle__summary-line-name">
              ${thumbHtml}
              ${this._esc(name)} (${pet.petType})
              ${pet.quantity > 1 ? `<span class="pet-bundle__summary-line-qty">&times;${pet.quantity}</span>` : ''}
            </span>
            <span>${this._formatMoney(lineTotal)}</span>
          </div>`;
        }).join('');
      }

      // Discount line
      if (this.summaryDiscount) {
        if (discountAmount > 0) {
          this.summaryDiscount.hidden = false;
          const el = this.summaryDiscount.querySelector('.pet-bundle__summary-discount-amount');
          if (el) el.textContent = `-${this._formatMoney(discountAmount)}`;
        } else {
          this.summaryDiscount.hidden = true;
        }
      }

      // Total (with list price strikethrough when discounted)
      if (this.totalPrice) {
        this.totalPrice.textContent = this._formatMoney(bundleTotal);
      }
      if (this.listPrice) {
        if (discountAmount > 0) {
          this.listPrice.textContent = this._formatMoney(basePrice * totalPets);
          this.listPrice.hidden = false;
        } else {
          this.listPrice.hidden = true;
        }
      }

      // Auto-save form state to localStorage (fire-and-forget)
      this._saveState();
    }

    _updateSavingsCallout(totalPets) {
      const discountAmount = this._getDiscountAmountCents(totalPets);
      const show = discountAmount > 0;
      const savingsText = show ? this._formatMoney(discountAmount) : null;

      [this.savingsCallout, this.savingsCalloutTier].forEach(el => {
        if (!el) return;
        el.classList.toggle('pet-bundle__savings-callout--visible', show);
      });
      [this.savingsAmount, this.savingsAmountTier].forEach(el => {
        if (!el || !savingsText) return;
        el.textContent = savingsText;
      });
    }

    _updateCardProgress() {
      const cards = this.cardsContainer.querySelectorAll('.pet-card');
      cards.forEach(card => {
        const progressFill = card.querySelector('.pet-card__progress-fill');
        const progressText = card.querySelector('.pet-card__progress-text');
        if (!progressFill || !progressText) return;

        const data = this._getCardData(card);
        const pondInstance = this._filePondInstances.get(String(data.index));

        let filled = 0;
        const total = 3; // name, eye color, photo
        if (data.petName) filled++;
        if (data.eyeColor) filled++;
        const hasFile = data.uploadUrl || (pondInstance && pondInstance.getFiles().length > 0);
        if (hasFile) filled++;

        const pct = Math.round((filled / total) * 100);
        progressFill.style.setProperty('width', pct + '%', 'important');
        progressFill.dataset.filled = String(filled);

        if (filled === total) {
          progressFill.classList.add('pet-card__progress-fill--complete');
          progressText.classList.add('pet-card__progress-text--complete');
          progressText.textContent = '✓ Complete';
        } else {
          progressFill.classList.remove('pet-card__progress-fill--complete');
          progressText.classList.remove('pet-card__progress-text--complete');
          progressText.textContent = `${filled}/${total} complete`;
        }
      });
    }

    _updateTierBadges(totalPets) {
      if (!this.tierBadges) return;
      this.tierBadges.querySelectorAll('.pet-bundle__tier-badge').forEach(badge => {
        const tier = parseInt(badge.dataset.tier, 10);
        const active = (tier === 5 && totalPets >= 5)
          || (tier === totalPets && totalPets >= 2 && totalPets <= 4);
        badge.classList.toggle('pet-bundle__tier-badge--active', active);
      });
    }

    /* ------------------------------------------------------------------ */
    /*  Validation (upload is REQUIRED, auto-clear errors on interaction)  */
    /* ------------------------------------------------------------------ */

    _validateCards() {
      const errors = [];
      const cards = this.cardsContainer.querySelectorAll('.pet-card');

      cards.forEach((card, i) => {
        const data = this._getCardData(card);
        const num = i + 1;

        // Name
        const nameInput = card.querySelector('.pet-card__name-input');
        if (!data.petName) {
          errors.push(`Pet ${num}: Please enter a pet name`);
          nameInput?.classList.add('pet-card__input--error');
        } else {
          nameInput?.classList.remove('pet-card__input--error');
        }

        // Eye color (from pill selection, stored in hidden input)
        const eyePills = card.querySelector('.pet-card__eye-pills');
        if (!data.eyeColor) {
          errors.push(`Pet ${num}: Please select an eye color`);
          eyePills?.classList.add('pet-card__eye-pills--error');
        } else {
          eyePills?.classList.remove('pet-card__eye-pills--error');
        }

        // Upload — REQUIRED (must be a successful HTTP URL, not just a local file)
        const uploadZone = card.querySelector('.pet-card__upload-zone');
        const pondInstance = this._filePondInstances.get(String(data.index));
        const hasUploadedUrl = data.uploadUrl && data.uploadUrl.startsWith('http');
        const hasLocalFile = pondInstance && pondInstance.getFiles().length > 0;

        if (!hasUploadedUrl) {
          if (hasLocalFile) {
            errors.push(`Pet ${num}: Photo is still uploading — please wait a moment and try again`);
            this._trackUploadIssue('submit_while_uploading', { pet: num });
          } else {
            errors.push(`Pet ${num}: Please upload a photo of your pet`);
          }
          uploadZone?.classList.add('pet-card__upload-zone--error');
        } else {
          uploadZone?.classList.remove('pet-card__upload-zone--error');
        }
      });

      return errors;
    }

    /* ------------------------------------------------------------------ */
    /*  Cart Submission                                                     */
    /* ------------------------------------------------------------------ */

    async _addToCart() {
      if (this._isSubmitting) return;
      this._hideError();

      // Check uploads in progress
      let uploading = false;
      this._filePondInstances.forEach(pond => {
        if (pond.getFiles().some(f => f.status === 3)) uploading = true;
      });
      if (uploading) {
        this._showError('Please wait for photo uploads to finish.');
        return;
      }

      const errors = this._validateCards();
      if (errors.length) {
        this._showError(errors[0]);
        return;
      }

      this._setLoading(true);

      try {
        const items = this._buildCartItems();
        const res = await fetch(this.config.routes.cartAddUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items }),
        });

        if (!res.ok) {
          let msg = 'Could not add items to cart.';
          try { const d = await res.json(); msg = d.description || d.message || msg; } catch (e) {}
          throw new Error(msg);
        }

        // Notify Dawn
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          try { publish(PUB_SUB_EVENTS.cartUpdate, { source: 'pet-bundle-builder' }); } catch (e) {}
        }
        document.dispatchEvent(new CustomEvent('cart:updated'));

        // Successfully added to cart — clear saved form state
        this._clearState();

        // Open cart drawer instead of redirecting to /cart
        this._setLoading(false);
        document.dispatchEvent(new CustomEvent('cart:open'));
      } catch (err) {
        this._showError(err.message);
        this._setLoading(false);
      }
    }

    async _buyNow() {
      if (this._isSubmitting) return;
      this._hideError();

      // Check uploads in progress
      let uploading = false;
      this._filePondInstances.forEach(pond => {
        if (pond.getFiles().some(f => f.status === 3)) uploading = true;
      });
      if (uploading) {
        this._showError('Please wait for photo uploads to finish.');
        return;
      }

      const errors = this._validateCards();
      if (errors.length) {
        this._showError(errors[0]);
        return;
      }

      this._setLoading(true, this.buyNowBtn);

      try {
        const items = this._buildCartItems();
        const res = await fetch(this.config.routes.cartAddUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items }),
        });

        if (!res.ok) {
          let msg = 'Could not add items to cart.';
          try { const d = await res.json(); msg = d.description || d.message || msg; } catch (e) {}
          throw new Error(msg);
        }

        // Notify Dawn
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          try { publish(PUB_SUB_EVENTS.cartUpdate, { source: 'pet-bundle-builder' }); } catch (e) {}
        }
        document.dispatchEvent(new CustomEvent('cart:updated'));

        // Successfully added to cart — clear saved form state
        this._clearState();

        window.location.href = '/checkout';
      } catch (err) {
        this._showError(err.message);
        this._setLoading(false, this.buyNowBtn);
      }
    }

    _buildCartItems() {
      const petData = this._getAllCardData();
      const total = petData.reduce((s, p) => s + p.quantity, 0);
      const discountAmount = this._getDiscountAmountCents(total);

      return petData.map((pet, i) => {
        const props = {};
        if (pet.petName) props['Pet Name'] = pet.petName;
        if (pet.eyeColor) props['Eye Color'] = pet.eyeColor;
        if (pet.uploadUrl && pet.uploadUrl.startsWith('http')) {
          props['Pet Photo'] = pet.uploadUrl;
        }

        return {
          id: parseInt(pet.variantId, 10),
          quantity: pet.quantity,
          properties: props,
        };
      });
    }

    /* ------------------------------------------------------------------ */
    /*  UI Helpers                                                         */
    /* ------------------------------------------------------------------ */

    _showError(msg) {
      if (this.errorEl) {
        this.errorEl.textContent = msg;
        this.errorEl.hidden = false;
        this.errorEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }

    _hideError() {
      if (this.errorEl) { this.errorEl.hidden = true; this.errorEl.textContent = ''; }
    }

    /**
     * Track upload issues via Klaviyo for alerting.
     * @param {'timeout'|'upload_error'|'submit_while_uploading'} type
     * @param {Object} details - { pet, error? }
     */
    _trackUploadIssue(type, details = {}) {
      try {
        const isMobile = window.innerWidth < 750;
        const payload = {
          type,
          pet_number: details.pet || 0,
          error_message: details.error || '',
          page_url: window.location.href,
          device: isMobile ? 'mobile' : 'desktop',
          user_agent: navigator.userAgent,
          timestamp: new Date().toISOString()
        };
        // Klaviyo JS API (loaded by Shopify app)
        window._learnq = window._learnq || [];
        window._learnq.push(['track', 'Photo Upload Issue', payload]);
      } catch (e) {
        // Silent — never block the user experience for tracking
      }
    }

    /**
     * Reset the upload stall-timeout. Called on file add + each progress event.
     * If no progress arrives within the stall window, warn the user. Mobile
     * networks are slower — give them more room.
     */
    _resetUploadTimeout(cardIndex, card) {
      const prev = this._uploadTimeouts.get(String(cardIndex));
      if (prev) clearTimeout(prev);

      const isMobile = window.innerWidth < 750;
      const STALL_MS = isMobile ? 60000 : 30000;
      const num = parseInt(cardIndex, 10) + 1;
      const uploadUrlInput = card.querySelector('.pet-card__upload-url');

      const timeoutId = setTimeout(() => {
        if (!uploadUrlInput || !uploadUrlInput.value || !uploadUrlInput.value.startsWith('http')) {
          this._showError(`Pet ${num}: Upload is taking longer than expected — please remove and re-upload your photo.`);
          this._trackUploadIssue('timeout', { pet: num });
        }
        this._uploadTimeouts.delete(String(cardIndex));
      }, STALL_MS);

      this._uploadTimeouts.set(String(cardIndex), timeoutId);
    }

    /* ------------------------------------------------------------------ */
    /*  Form Persistence — localStorage auto-save/restore                  */
    /* ------------------------------------------------------------------ */

    _storageKey() {
      return `pet-bundle-state-v1:${this.sectionId || 'default'}`;
    }

    /**
     * Serialize current form state and save to localStorage.
     * Called on every meaningful field change.
     */
    _saveState() {
      try {
        if (!this.cardsContainer) return;
        const cards = Array.from(this.cardsContainer.querySelectorAll('.pet-card')).map((card) => {
          const data = this._getCardData(card);
          const activeSpecies = card.querySelector('.pet-card__species-pill--active');
          return {
            petName: data.petName,
            eyeColor: data.eyeColor,
            species: activeSpecies?.dataset.petType || 'Dog',
            variantId: data.variantId,
            uploadUrl: (data.uploadUrl && data.uploadUrl.startsWith('http')) ? data.uploadUrl : '',
            thumbUrl: (data.thumbUrl && data.thumbUrl.startsWith('http')) ? data.thumbUrl : '',
            quantity: data.quantity,
            eyeUserChose: card.dataset.eyeUserChose === 'true'
          };
        });
        const state = {
          version: 1,
          timestamp: Date.now(),
          petCount: this._currentPetCount,
          cards
        };
        localStorage.setItem(this._storageKey(), JSON.stringify(state));
      } catch (e) {
        // Silently ignore quota errors / private mode — don't break the UX
      }
    }

    /**
     * Load saved state from localStorage and apply to the form.
     * Called once on connectedCallback. Expires after 24 hours.
     */
    _restoreState() {
      try {
        const raw = localStorage.getItem(this._storageKey());
        if (!raw) return;

        const state = JSON.parse(raw);
        if (!state || state.version !== 1 || !Array.isArray(state.cards)) return;

        // 24h TTL
        const age = Date.now() - (state.timestamp || 0);
        if (age > 24 * 60 * 60 * 1000) {
          this._clearState();
          return;
        }

        // Nothing to restore if all fields were empty
        const hasData = state.cards.some(c => c.petName || c.uploadUrl || c.eyeUserChose);
        if (!hasData) return;

        // Ensure we have the right number of cards
        const targetCount = Math.max(1, Math.min(10, state.petCount || state.cards.length));
        this._setPetCount(targetCount);
        this._syncCountSelector();

        // Apply saved fields to each card
        const cards = Array.from(this.cardsContainer.querySelectorAll('.pet-card'));
        cards.forEach((card, i) => {
          const saved = state.cards[i];
          if (!saved) return;

          // Species pill + variant
          if (saved.species) {
            card.querySelectorAll('.pet-card__species-pill').forEach(p => {
              const match = p.dataset.petType === saved.species;
              p.classList.toggle('pet-card__species-pill--active', match);
            });
            const variantInput = card.querySelector('.pet-card__variant-input');
            if (variantInput && saved.variantId) variantInput.value = saved.variantId;
          }

          // Pet name
          const nameInput = card.querySelector('.pet-card__name-input');
          if (nameInput && saved.petName) nameInput.value = saved.petName;

          // Eye color
          if (saved.eyeColor) {
            card.querySelectorAll('.pet-card__eye-pill').forEach(p => {
              p.classList.toggle('pet-card__eye-pill--active', (p.dataset.color || '') === saved.eyeColor);
            });
            const eyeValue = card.querySelector('.pet-card__eye-value');
            if (eyeValue) eyeValue.value = saved.eyeColor;
          }
          if (saved.eyeUserChose) card.dataset.eyeUserChose = 'true';

          // Quantity
          const qtyInput = card.querySelector('.pet-card__qty-input');
          if (qtyInput && saved.quantity) qtyInput.value = saved.quantity;

          // Upload URL (only server-hosted URLs — data URLs aren't restored)
          if (saved.uploadUrl && saved.uploadUrl.startsWith('http')) {
            const uploadInput = card.querySelector('.pet-card__upload-url');
            if (uploadInput) uploadInput.value = saved.uploadUrl;
          }
          if (saved.thumbUrl && saved.thumbUrl.startsWith('http')) {
            card.dataset.thumbUrl = saved.thumbUrl;
          }
        });

        this._updateCardBadgeThumbnails();
      } catch (e) {
        // Corrupt state — discard
        this._clearState();
      }
    }

    _clearState() {
      try { localStorage.removeItem(this._storageKey()); } catch (e) {}
    }

    /**
     * Update the per-card upload progress indicator.
     * @param {HTMLElement} card
     * @param {number|null} progress - 0..1 or null to hide
     */
    _updateUploadProgress(card, progress) {
      if (!card) return;
      const zone = card.querySelector('.pet-card__upload-zone');
      if (!zone) return;

      let bar = zone.parentElement.querySelector('.pet-card__upload-progress');
      if (progress == null || progress >= 1) {
        if (bar) bar.remove();
        return;
      }

      if (!bar) {
        bar = document.createElement('div');
        bar.className = 'pet-card__upload-progress';
        bar.innerHTML = `
          <div class="pet-card__upload-progress-track"><div class="pet-card__upload-progress-fill"></div></div>
          <span class="pet-card__upload-progress-label">Uploading 0%</span>
        `;
        zone.parentElement.appendChild(bar);
      }
      const pct = Math.max(0, Math.min(100, Math.round(progress * 100)));
      const fill = bar.querySelector('.pet-card__upload-progress-fill');
      const label = bar.querySelector('.pet-card__upload-progress-label');
      if (fill) fill.style.width = pct + '%';
      if (label) label.textContent = `Uploading ${pct}%`;
    }

    _setLoading(on, btn) {
      this._isSubmitting = on;
      const target = btn || this.addToCartBtn;
      [this.addToCartBtn, this.buyNowBtn].forEach(b => {
        if (!b) return;
        b.disabled = on;
        if (b === target) {
          b.classList.toggle('pet-bundle__submit--loading', on);
          const spinner = b.querySelector('.pet-bundle__submit-loading');
          if (spinner) spinner.hidden = !on;
        }
      });
    }

    _esc(str) {
      const d = document.createElement('div');
      d.textContent = str;
      return d.innerHTML;
    }
  }

  customElements.define('pet-bundle-builder', PetBundleBuilder);
}
